import { Logger } from "./logger";
import { notifier } from "./notifier/notifier";
import { FormBase } from "../util/form-base-form";
import { CommonUtils } from "../util/common-utils";
import { getApiResponseSchema } from "../api/generated/response-registry";

type UnknownRecord = Record<string, unknown>;

class ApiBaseForm extends FormBase {
    public getMeta(root: ParentNode, name: string): HTMLMetaElement | null {
        return this.queryElement(root, `meta[name="${name}"]`, HTMLMetaElement);
    }

    public getTokenInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[name="_token"]');
    }
}

const apiBaseForm = new ApiBaseForm();

export type RequestPayload = UnknownRecord | FormData | string | null;

export enum HttpMethod {
    Get = "GET",
    Post = "POST",
    Put = "PUT",
    Patch = "PATCH",
    Delete = "DELETE",
    Head = "HEAD",
}

interface ApiHeaders extends Record<string, string> {
    Accept: string;
    "X-Requested-With": string;
    "X-CSRF-TOKEN"?: string;
    "Content-Type"?: string;
}

interface RequestFetchOptions extends RequestInit {
    headers: ApiHeaders;
    cache?: RequestCache;
    noSpinner?: boolean;
}

export interface ApiRequestOptions {
    noSpinner?: boolean;
}

export interface ApiRequestWithErrorHandlingOptions extends ApiRequestOptions {
    _timeout?: number;
    showErrors?: boolean;
    fallbackMessage?: string;
}

interface ErrorResponsePayload {
    message?: string;
    error?: string;
    redirect_to?: string;
    errors?: unknown;
}

export interface FormRequestJsonResponse extends ErrorResponsePayload {
    success?: boolean;
    data?: UnknownRecord;
    redirect?: string;
    [key: string]: unknown;
}

class APIError extends Error {
    public status: number;

    public constructor(message: string, status: number) {
        super(message);
        this.name = "APIError";
        this.status = status;
    }
}

class NetworkError extends Error {
    public constructor(message: string) {
        super(message);
        this.name = "NetworkError";
    }
}

class AuthenticationError extends Error {
    public constructor(message: string) {
        super(message);
        this.name = "AuthenticationError";
    }
}

class ApiResponseValidationError extends Error {
    public readonly status: number;

    public readonly url: string;

    public readonly issues: string[];

    public constructor(message: string, status: number, url: string, issues: string[]) {
        super(message);
        this.name = "ApiResponseValidationError";
        this.status = status;
        this.url = url;
        this.issues = issues;
    }
}

class ApiBase {
    protected static toHttpMethod(method: string | HttpMethod): HttpMethod {
        const normalizedMethod = String(method).toUpperCase();

        switch (normalizedMethod) {
            case HttpMethod.Get:
                return HttpMethod.Get;
            case HttpMethod.Post:
                return HttpMethod.Post;
            case HttpMethod.Put:
                return HttpMethod.Put;
            case HttpMethod.Patch:
                return HttpMethod.Patch;
            case HttpMethod.Delete:
                return HttpMethod.Delete;
            case HttpMethod.Head:
                return HttpMethod.Head;
            default:
                throw new Error(`Unsupported HTTP method: ${String(method)}`);
        }
    }

    protected static buildErrorMessageFromPayload(payload: ErrorResponsePayload, fallbackMessage: string): string {
        const errors = payload?.errors;
        let errorText = "";

        if (Array.isArray(errors)) {
            errorText = errors.map((message) => String(message)).join("; ");
        } else if (errors && typeof errors === "object") {
            const lines: string[] = [];
            for (const [field, messages] of Object.entries(errors as UnknownRecord)) {
                if (Array.isArray(messages)) {
                    for (const message of messages) {
                        lines.push(`${field}: ${String(message)}`);
                    }
                } else if (messages !== undefined && messages !== null) {
                    lines.push(`${field}: ${String(messages)}`);
                }
            }
            errorText = lines.join("; ");
        }

        const message = typeof payload.message === "string" ? payload.message.trim() : "";
        const error = typeof payload.error === "string" ? payload.error.trim() : "";
        const isGenericValidationMessage = message.toLowerCase() === "validation failed";

        if (errorText) {
            if (message && !isGenericValidationMessage && message !== errorText) {
                return `${message}: ${errorText}`;
            }

            return errorText;
        }

        return message || error || fallbackMessage;
    }

    protected static describeRequestBody(body: RequestPayload): unknown {
        if (!(body instanceof FormData)) {
            return body;
        }

        const serialized: UnknownRecord = {};

        for (const [key, value] of body.entries()) {
            const normalizedValue = value instanceof File ? `[File:${value.name}]` : String(value);
            const current = serialized[key];

            if (current === undefined) {
                serialized[key] = normalizedValue;
                continue;
            }

            if (Array.isArray(current)) {
                serialized[key] = [...current, normalizedValue];
                continue;
            }

            serialized[key] = [current, normalizedValue];
        }

        return serialized;
    }

    protected static formatValidationIssue(issue: {
        path: Array<string | number>;
        message: string;
        code?: string;
        unionErrors?: Array<{ issues?: Array<unknown> }>;
    }): string {
        if (issue.code === "invalid_union" && Array.isArray(issue.unionErrors)) {
            for (const unionError of issue.unionErrors) {
                const nestedIssue = Array.isArray(unionError.issues) ? unionError.issues[0] : null;
                if (
                    nestedIssue &&
                    typeof nestedIssue === "object" &&
                    "path" in nestedIssue &&
                    "message" in nestedIssue
                ) {
                    return this.formatValidationIssue(
                        nestedIssue as {
                            path: Array<string | number>;
                            message: string;
                            code?: string;
                            unionErrors?: Array<{ issues?: Array<unknown> }>;
                        },
                    );
                }
            }
        }

        const issuePath = issue.path.length > 0 ? issue.path.map((segment) => String(segment)).join(".") : "<root>";

        return `${issuePath}: ${issue.message}`;
    }

    protected static toFormData(value: RequestPayload): FormData {
        if (value instanceof FormData) {
            const cloned = new FormData();
            for (const [key, entry] of value.entries()) {
                cloned.append(key, entry);
            }
            return cloned;
        }

        const formData = new FormData();
        if (value === null || value === undefined) {
            return formData;
        }

        if (typeof value === "string") {
            const stringValue = value;
            try {
                const parsed = JSON.parse(stringValue) as UnknownRecord;
                value = parsed;
            } catch {
                formData.append("payload", stringValue);
                return formData;
            }
        }

        if (typeof value === "object") {
            for (const [key, entry] of Object.entries(value)) {
                if (entry === null || entry === undefined) continue;
                if (typeof entry === "object") {
                    formData.append(key, JSON.stringify(entry));
                } else {
                    formData.append(key, String(entry));
                }
            }
        }

        return formData;
    }

    protected static getCsrfToken(): string | null {
        const metaTag = apiBaseForm.getMeta(document, "csrf-token");
        if (metaTag && metaTag.getAttribute("content")) {
            return metaTag.getAttribute("content");
        }

        const tokenField = apiBaseForm.getTokenInput(document);
        if (tokenField instanceof HTMLInputElement && tokenField.value) {
            return tokenField.value;
        }

        throw new Error("CSRF token not found");
    }

    protected static getApiHeaders(): ApiHeaders {
        const csrfToken = this.getCsrfToken();
        const headers: ApiHeaders = {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
        };

        if (csrfToken) {
            headers["X-CSRF-TOKEN"] = csrfToken;
        }

        return headers;
    }

    protected static getJsonHeaders(): ApiHeaders {
        const headers = this.getApiHeaders();
        headers["Content-Type"] = "application/json";
        return headers;
    }

    protected static shouldUseMethodOverride(method: HttpMethod): boolean {
        return method === HttpMethod.Put || method === HttpMethod.Delete || method === HttpMethod.Patch;
    }

    protected static normalizeRequestForTransport(
        method: HttpMethod,
        body: RequestPayload,
    ): { method: HttpMethod; body: RequestPayload } {
        if (!this.shouldUseMethodOverride(method)) {
            return { method, body };
        }

        const formData = this.toFormData(body);
        formData.set("_method", method);
        return {
            method: HttpMethod.Post,
            body: formData,
        };
    }

    protected static getFetchOptions(method: HttpMethod, body: RequestPayload = null): RequestFetchOptions {
        const options: RequestFetchOptions = {
            method,
            credentials: "include",
            headers: this.getApiHeaders(),
        };

        if (body) {
            if (body instanceof FormData) {
                options.headers = this.getApiHeaders();

                try {
                    const csrfToken = this.getCsrfToken();
                    if (csrfToken) {
                        body.set("_token", csrfToken);
                    }
                } catch (_error) {
                    void _error;
                }

                options.body = body;
            } else {
                options.headers = this.getJsonHeaders();
                options.body = typeof body === "string" ? body : JSON.stringify(body);
            }
        } else {
            options.headers = this.getApiHeaders();
            if (options.method === HttpMethod.Get || options.method === HttpMethod.Head) {
                options.cache = "no-store";
            }
        }

        return options;
    }

    protected static async apiRequest(
        url: string,
        method: string | HttpMethod,
        body: RequestPayload = null,
        options: ApiRequestOptions = {},
    ): Promise<Response> {
        const resolvedUrl = CommonUtils.resolveUrl(url);
        const requestedMethod = this.toHttpMethod(method);
        const normalizedRequest = this.normalizeRequestForTransport(requestedMethod, body);
        const normalizedMethod = normalizedRequest.method;
        const normalizedBody = normalizedRequest.body;

        const fetchOptions = this.getFetchOptions(normalizedMethod, normalizedBody);

        if (options.noSpinner) {
            fetchOptions.noSpinner = true;
        }

        let actualMethod: string = normalizedMethod;
        if (normalizedBody instanceof FormData && normalizedBody.has("_method")) {
            const methodOverride = normalizedBody.get("_method");
            actualMethod = typeof methodOverride === "string" ? methodOverride : normalizedMethod;
        }

        Logger.info("Request:", {
            method: actualMethod,
            url: resolvedUrl,
            headers: fetchOptions.headers,
            body: this.describeRequestBody(normalizedBody),
        });

        try {
            const response = await fetch(resolvedUrl, fetchOptions);
            const responseBody = await response
                .clone()
                .json()
                .catch(() => response.clone().text());

            const responseHeaders: Record<string, string> = {};
            response.headers.forEach((value, key) => {
                responseHeaders[key] = value;
            });

            const logResponse = {
                method: actualMethod,
                url: resolvedUrl,
                status: response.status,
                headers: responseHeaders,
                body: responseBody,
            };

            if (response.ok) {
                const contentType = response.headers.get("content-type") || "";
                const isJsonResponse = contentType.includes("application/json");

                if (isJsonResponse) {
                    const pathname = new URL(resolvedUrl, window.location.origin).pathname;
                    const responseSchema = getApiResponseSchema(actualMethod, pathname, response.status);

                    if (responseSchema) {
                        const validationResult = responseSchema.safeParse(responseBody);
                        if (!validationResult.success) {
                            const issues = validationResult.error.issues.map((issue) =>
                                this.formatValidationIssue(issue),
                            );

                            Logger.error("API response contract mismatch", {
                                method: actualMethod,
                                url: resolvedUrl,
                                status: response.status,
                                issues,
                                body: responseBody,
                            });

                            throw new ApiResponseValidationError(
                                `API response validation failed for ${actualMethod} ${pathname}`,
                                response.status,
                                resolvedUrl,
                                issues,
                            );
                        }
                    }
                }

                Logger.log("✅Response:", logResponse);
            } else if (response.status === 401) {
                Logger.warn("CSRF token being sent:", fetchOptions.headers["X-CSRF-TOKEN"]);
                Logger.warn("Error Response:", logResponse);
            } else {
                Logger.error("🟡Response:", logResponse);
            }

            return response;
        } catch (error) {
            Logger.error(error instanceof Error ? error : { error });
            throw error;
        }
    }

    protected static async apiGet(url: string, options: ApiRequestOptions = {}): Promise<Response> {
        return this.apiRequest(url, HttpMethod.Get, null, options);
    }

    protected static async apiPost(
        url: string,
        body: RequestPayload = null,
        options: ApiRequestOptions = {},
    ): Promise<Response> {
        return this.apiRequest(url, HttpMethod.Post, body, options);
    }

    protected static async apiPut(url: string, body: RequestPayload = null): Promise<Response> {
        return this.apiRequest(url, HttpMethod.Put, body);
    }

    protected static async apiDelete(url: string, options: ApiRequestOptions = {}): Promise<Response> {
        return this.apiRequest(url, HttpMethod.Delete, null, options);
    }

    protected static async formRequest(
        url: string,
        formData: FormData,
        method: string | HttpMethod = HttpMethod.Post,
    ): Promise<FormRequestJsonResponse | string> {
        try {
            const response = await this.apiRequest(url, method, formData);

            if (!response.ok) {
                if (response.status === 401 || response.status === 419) {
                    this.handleSessionExpired();
                }

                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

                try {
                    if (response.headers.get("content-type")?.includes("application/json")) {
                        const errorData = (await response.json()) as ErrorResponsePayload;
                        errorMessage = this.buildErrorMessageFromPayload(errorData, errorMessage);
                    }
                } catch (parseError) {
                    console.error(parseError);
                }

                throw new APIError(errorMessage, response.status);
            }

            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                return response.json();
            }

            return response.text();
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    protected static handleSessionExpired(): never {
        setTimeout(() => window.location.reload(), 2000);
        throw new AuthenticationError("Authentication required");
    }

    protected static interceptAuthErrors(response: Response): Response {
        if (response.status === 401 || response.status === 419) {
            this.handleSessionExpired();
        }
        return response;
    }

    protected static async apiRequestWithErrorHandling(
        url: string,
        method: string | HttpMethod,
        body: RequestPayload = null,
        options: ApiRequestWithErrorHandlingOptions = {},
    ): Promise<Response> {
        const { _timeout = 30000, showErrors = true, fallbackMessage = "Request failed" } = options;
        void _timeout;

        try {
            const response = await this.apiRequest(url, method, body, options);

            if (!response.ok) {
                if (response.status === 401 || response.status === 419) {
                    this.handleSessionExpired();
                }

                let errorMessage = fallbackMessage;
                try {
                    if (response.headers.get("content-type")?.includes("application/json")) {
                        const errorData = (await response.json()) as ErrorResponsePayload;
                        errorMessage = this.buildErrorMessageFromPayload(errorData, fallbackMessage);
                    }
                } catch (parseError) {
                    console.error(parseError);
                }

                if (showErrors) {
                    notifier.error(errorMessage);
                }

                throw new APIError(errorMessage, response.status);
            }

            return response;
        } catch (error) {
            console.error(error);
            if (error instanceof TypeError && error.message.includes("fetch")) {
                const networkMessage = "network_connection_failed";
                if (showErrors) {
                    notifier.error(networkMessage);
                }
                throw new NetworkError(networkMessage);
            }
            throw error;
        }
    }

    protected apiRequest(
        url: string,
        method: string | HttpMethod,
        body: RequestPayload = null,
        options: ApiRequestOptions = {},
    ): Promise<Response> {
        return (this.constructor as typeof ApiBase).apiRequest(url, method, body, options);
    }

    protected apiGet(url: string, options: ApiRequestOptions = {}): Promise<Response> {
        return (this.constructor as typeof ApiBase).apiGet(url, options);
    }

    protected apiPost(url: string, body: RequestPayload = null, options: ApiRequestOptions = {}): Promise<Response> {
        return (this.constructor as typeof ApiBase).apiPost(url, body, options);
    }

    protected apiPut(url: string, body: RequestPayload = null): Promise<Response> {
        return (this.constructor as typeof ApiBase).apiPut(url, body);
    }

    protected apiDelete(url: string, options: ApiRequestOptions = {}): Promise<Response> {
        return (this.constructor as typeof ApiBase).apiDelete(url, options);
    }

    protected formRequest(url: string, formData: FormData, method = "POST"): Promise<FormRequestJsonResponse | string> {
        return (this.constructor as typeof ApiBase).formRequest(url, formData, method);
    }

    protected apiRequestWithErrorHandling(
        url: string,
        method: string | HttpMethod,
        body: RequestPayload = null,
        options: ApiRequestWithErrorHandlingOptions = {},
    ): Promise<Response> {
        return (this.constructor as typeof ApiBase).apiRequestWithErrorHandling(url, method, body, options);
    }
}

export { ApiBase, APIError, NetworkError, AuthenticationError, ApiResponseValidationError };
